import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tags, Plus, Edit, Archive, ArchiveRestore, Save, X, AlertTriangle } from "lucide-react";

export interface TarifaCategoria {
  id: string;
  empresa_id: string;
  nombre: string;
  coste_hora: number;
  coste_hora_extra: number;
  importe_furgoneta: number;
  activo: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const fmtEur = (v: number | null | undefined) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

interface Draft {
  id?: string;
  nombre: string;
  coste_hora: string;
  coste_hora_extra: string;
  importe_furgoneta: string;
}

const emptyDraft: Draft = {
  nombre: "",
  coste_hora: "",
  coste_hora_extra: "",
  importe_furgoneta: "0",
};

export const CategoriasTarifaDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [categorias, setCategorias] = useState<TarifaCategoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const cargar = async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("tarifas_categoria" as any)
      .select("*")
      .eq("empresa_id", perfil.empresa_id)
      .order("activo", { ascending: false })
      .order("nombre");
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setCategorias((data || []) as unknown as TarifaCategoria[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      cargar();
      setDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startNew = () => setDraft({ ...emptyDraft });
  const startEdit = (c: TarifaCategoria) =>
    setDraft({
      id: c.id,
      nombre: c.nombre,
      coste_hora: String(c.coste_hora),
      coste_hora_extra: String(c.coste_hora_extra),
      importe_furgoneta: String(c.importe_furgoneta ?? 0),
    });

  const guardar = async () => {
    if (!draft || !perfil?.empresa_id) return;
    const ch = parseFloat(draft.coste_hora);
    const che = parseFloat(draft.coste_hora_extra);
    const furg = parseFloat(draft.importe_furgoneta || "0");
    if (!draft.nombre.trim()) {
      toast({ title: "Falta el nombre", variant: "destructive" });
      return;
    }
    if (isNaN(ch) || ch < 0 || isNaN(che) || che < 0 || isNaN(furg) || furg < 0) {
      toast({ title: "Valores numéricos inválidos", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload: any = {
      nombre: draft.nombre.trim(),
      coste_hora: ch,
      coste_hora_extra: che,
      importe_furgoneta: furg,
    };
    let error;
    if (draft.id) {
      ({ error } = await supabase
        .from("tarifas_categoria" as any)
        .update(payload)
        .eq("id", draft.id));
    } else {
      payload.empresa_id = perfil.empresa_id;
      ({ error } = await supabase.from("tarifas_categoria" as any).insert(payload));
    }
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: draft.id ? "Categoría actualizada" : "Categoría creada",
      description: draft.id
        ? "Los técnicos asignados recibirán la nueva tarifa desde hoy."
        : "Ya puedes asignarla a tus técnicos.",
    });
    setDraft(null);
    cargar();
  };

  const toggleActivo = async (c: TarifaCategoria) => {
    const { error } = await supabase
      .from("tarifas_categoria" as any)
      .update({ activo: !c.activo })
      .eq("id", c.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      cargar();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Categorías de tarifa
          </DialogTitle>
          <DialogDescription>
            Define una vez tarifas tipo "Oficial 1ª", "Ayudante"… y asígnalas a tus técnicos
            con un click. Editar una categoría aplica la nueva tarifa a los técnicos asignados
            desde hoy. No afecta a jornadas pasadas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          {!draft && (
            <Button onClick={startNew} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Nueva categoría
            </Button>
          )}
        </div>

        {draft && (
          <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-1">
                <Label>Nombre *</Label>
                <Input
                  value={draft.nombre}
                  onChange={(e) => setDraft({ ...draft, nombre: e.target.value })}
                  placeholder="Oficial 1ª"
                />
              </div>
              <div>
                <Label>Coste/h (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.coste_hora}
                  onChange={(e) => setDraft({ ...draft, coste_hora: e.target.value })}
                />
              </div>
              <div>
                <Label>Hora extra (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.coste_hora_extra}
                  onChange={(e) => setDraft({ ...draft, coste_hora_extra: e.target.value })}
                />
              </div>
              <div>
                <Label>Furgoneta (€/jornada)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.importe_furgoneta}
                  onChange={(e) =>
                    setDraft({ ...draft, importe_furgoneta: e.target.value })
                  }
                />
              </div>
            </div>
            {draft.id && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                <span>
                  Al guardar, los técnicos con esta categoría recibirán automáticamente
                  una nueva tarifa con estos precios desde hoy.
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
                <X className="h-4 w-4 mr-1" />
                Cancelar
              </Button>
              <Button size="sm" onClick={guardar} disabled={saving}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Coste/h</TableHead>
                <TableHead className="text-right">Hora extra</TableHead>
                <TableHead className="text-right">Furgoneta</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    Cargando…
                  </TableCell>
                </TableRow>
              ) : categorias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6 italic">
                    Aún no hay categorías. Crea la primera para empezar.
                  </TableCell>
                </TableRow>
              ) : (
                categorias.map((c) => (
                  <TableRow key={c.id} className={!c.activo ? "opacity-60" : ""}>
                    <TableCell className="font-medium">{c.nombre}</TableCell>
                    <TableCell className="text-right">{fmtEur(c.coste_hora)}</TableCell>
                    <TableCell className="text-right">{fmtEur(c.coste_hora_extra)}</TableCell>
                    <TableCell className="text-right">{fmtEur(c.importe_furgoneta)}</TableCell>
                    <TableCell>
                      {c.activo ? (
                        <Badge variant="secondary">Activa</Badge>
                      ) : (
                        <Badge variant="outline">Archivada</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(c)}
                        disabled={!!draft}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActivo(c)}>
                        {c.activo ? (
                          <Archive className="h-4 w-4" />
                        ) : (
                          <ArchiveRestore className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
